import type { ExportConfigurationContextValue } from './ExportConfigurationProvider'

import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronRightIcon, InfoIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useAccess } from '@/entities/session'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	FormProvider,
	RHFSelect,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { Loader } from '@/shared/components/ui/loader'
import {
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import { ampli } from '@/shared/lib/ampli'

import { useExportConfiguration } from './ExportConfigurationProvider'

type ExportParamsModalContentProps = {
	isAdditional?: boolean
	isLoading?: boolean
	onSubmit(data: ExportParamsFormData): void
	onClose(): void
	defaultValues: Partial<ExportParamsFormData>
	options: ExportConfigurationContextValue['options']
}
export function ExportParamsModalContent({
	onSubmit,
	onClose,
	isLoading,
	defaultValues,
	options,
	isAdditional,
}: ExportParamsModalContentProps) {
	const { t } = useTranslation()

	const {
		kind,
		state: { exportDestination, setExportDestination },
	} = useExportConfiguration()
	const { checkPermissons } = useAccess()
	const methods = useForm<ExportParamsFormData>({
		defaultValues: {
			fields_preset: 'all',
			format: 'xlsx',
			aggregation: 'product',
			...defaultValues,
		},
		resolver: zodResolver(ExportParamsSchema),
	})
	const handleSubmit = methods.handleSubmit(onSubmit)
	if (isLoading) {
		return (
			<ModalContent data-testid='export-data-modal-form'>
				<div className='flex items-center justify-center py-44'>
					<Loader />
				</div>
			</ModalContent>
		)
	}

	return (
		<ModalContent data-testid='export-data-modal-form'>
			<ModalHeader>
				<ModalTitle>
					{t('export.params.modal_title', { defaultValue: 'Download report' })}
				</ModalTitle>
			</ModalHeader>
			<ErrorBoundary
				fallback={
					<div className='flex flex-col justify-center py-20 text-center'>
						<p className='text-lg font-bold'>{t('fatal_error_title')}🐞</p>
						<p className='text-muted-foreground'>{t('fatal_error_desc')}</p>
					</div>
				}
			>
				<ModalBody>
					<Tabs
						value={exportDestination}
						onValueChange={setExportDestination as (value: string) => void}
					>
						{!isAdditional && (
							<TabsList className='w-full'>
								<TabsTrigger
									data-tab='local'
									value='local'
									onClick={() => {
										ampli.exportParamsChangeType({
											export_kind: kind,
											type: 'local',
										})
									}}
								>
									{t('app.my_computer', { defaultValue: 'My computer' })}
								</TabsTrigger>
								<TabsTrigger
									data-tab='external'
									value='external'
									disabled={!checkPermissons(['RUN_EXPORT'])}
									onClick={() => {
										ampli.exportParamsChangeType({
											export_kind: kind,
											type: 'external',
										})
									}}
								>
									{t('app.external_storage', {
										defaultValue: 'External storage',
									})}
								</TabsTrigger>
							</TabsList>
						)}
						<TabsContent value='local' className='pt-4'>
							<FormProvider
								methods={methods}
								onSubmit={handleSubmit}
								className='grid gap-3'
							>
								<RHFTextField
									required
									label={t('app.export_name', { defaultValue: 'Export name' })}
									className='w-full'
									autoComplete='off'
									name='name'
								/>
								<RHFSelect
									testId='export-columns-set-select'
									label={t('app.columns_set', { defaultValue: 'Columns set' })}
									className='w-full'
									name='fields_preset'
									options={options['fields_preset']}
								/>
								<RHFSelect
									testId='export-file-format-select'
									className='w-full'
									label={t('app.file_format', { defaultValue: 'File format' })}
									name='format'
									options={options['format']}
								/>
								{!isAdditional && (
									<>
										<RHFSelect
											className='w-full'
											label={t('app.group_by', { defaultValue: 'Group by' })}
											name='aggregation'
											options={options['aggregation']}
										/>
										{methods.watch('aggregation') === 'pricing_line' && (
											<Alert variant='warning'>
												<AlertDescription>
													{t('export.params.modal_group_by.pricing_line.hint', {
														defaultValue:
															'Grouping by "Pricing line" will disable "sku" and "title" columns',
													})}
												</AlertDescription>
											</Alert>
										)}
									</>
								)}
							</FormProvider>
						</TabsContent>
						<TabsContent value='external' className='pt-4'>
							<Alert variant='info'>
								<InfoIcon />
								<AlertDescription>
									{t('export.params.modal_external_storage_hint', {
										defaultValue:
											'Our integration team will process report to an external storage specified in the contract',
									})}
								</AlertDescription>
							</Alert>
						</TabsContent>
					</Tabs>
				</ModalBody>
				<ModalFooter className='justify-between'>
					<Button onClick={onClose} data-testid='export-data-modal-cancel'>
						{t('general_cancel')}
					</Button>
					{exportDestination === 'external' ? (
						<Button
							variant='primary'
							onClick={handleSubmit}
							data-testid='export-data-modal-export'
						>
							{t('app.export', { defaultValue: 'Export' })}
						</Button>
					) : (
						<Button
							variant='primary'
							onClick={handleSubmit}
							data-testid='export-data-modal-next'
						>
							{t('general_next')}
							<ChevronRightIcon />
						</Button>
					)}
				</ModalFooter>
			</ErrorBoundary>
		</ModalContent>
	)
}

export const ExportParamsSchema = z.object({
	name: z.string().min(1),
	fields_preset: z.string().min(1),
	format: z.enum(['xlsx', 'csv']),
	aggregation: z.enum(['product', 'pricing_line']),
})
export type ExportParamsFormData = z.infer<typeof ExportParamsSchema>
